/** @format */

import axios from "axios";
import {
	ICircleLeaderApplication,
	IAccountVerificationPayload,
} from "../../utility/types/circleType";
import { API_BASE_URL } from "../../axiosConfig/apiUrl";
import { isAuthRequiredResponse } from "../../axiosConfig/axiosInterceptor";
import { ViewAndSearchAddRequestSendOutQueryPayload } from "../../utility/types/addRequestType";
import { IJoinRequestPayload } from "../../utility/types/joinRequestType";

export const submitCircleLeaderApplication = async (
	payload: ICircleLeaderApplication,
) => {
	try {
		const response = await axios.post(
			`${API_BASE_URL}/applications/union-leader`,
			payload,
			{ withCredentials: true },
		);
		return response.data.application;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitAccountVerification = async (
	payload: IAccountVerificationPayload,
) => {
	try {
		const response = await axios.post(`${API_BASE_URL}/verification`, payload, {
			withCredentials: true,
		});

		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitGetAccountVerificationStatus = async (userId: string) => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/verification/user/${userId}`,
			{
				withCredentials: true,
			},
		);

		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitCreateCircle = async (payload: FormData) => {
	try {
		const response = await axios.post(`${API_BASE_URL}/union`, payload, {
			withCredentials: true,
		});

		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitFetchCircleByLeader = async (leaderId: string) => {
	try {
		const url = `${API_BASE_URL}/union/${leaderId}/unions`;

		const response = await axios.get(url, { withCredentials: true });

		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitFetchCircleById = async (unionId: string) => {
	try {
		const response = await axios.get(`${API_BASE_URL}/union/${unionId}`, {
			withCredentials: true,
		});
		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitFetchCircle = async (userId: string, circleId: string[]) => {
	try {
		const queryString = `circleId=${circleId.join(",")}`;

		const url = `${API_BASE_URL}/add-requests/union/${userId}?${queryString}`;

		const response = await axios.get(url, { withCredentials: true });

		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitFetchAddRequest = async (userId: string) => {
	try {
		const url = `${API_BASE_URL}/add-requests/${userId}`;
		const response = await axios.get(url, { withCredentials: true });

		return response.data.memberRequest;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitAcceptAddRequest = async (
	requestId: string,
	hasAgreedToCircleTerms: boolean,
) => {
	try {
		const url = `${API_BASE_URL}/add-requests/${requestId}/accept`;
		const response = await axios.post(
			url,
			{ hasAgreedToCircleTerms },
			{ withCredentials: true },
		);
		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const denyAddRequestService = async (requestId: string) => {
	try {
		const url = `${API_BASE_URL}/add-requests/${requestId}/deny`;
		const response = await axios.post(url, {}, { withCredentials: true });
		return response.data.updatedRequest;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitFetchAllCircles = async () => {
	try {
		const response = await axios.get(`${API_BASE_URL}/union`, {
			withCredentials: true,
		});

		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitLeaderSendAddRequest = async ({
	unionId,
	memberToAddId,
}: {
	unionId: string;
	memberToAddId: string;
}) => {
	try {
		const url = `${API_BASE_URL}/union/${unionId}/send-add-request`;
		const response = await axios.post(
			url,
			{ memberToAddId },
			{ withCredentials: true },
		);

		return response.data.addRequest;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitUpdateCircle = async (
	unionId: string,
	payload: FormData,
) => {
	try {
		const url = `${API_BASE_URL}/union/${unionId}`;
		const response = await axios.put(url, payload, { withCredentials: true });
		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitViewAndSearchAddRequestSendOut = async (
	unionId: string,
	searchValues?: ViewAndSearchAddRequestSendOutQueryPayload,
) => {
	let response;
	try {
		const queryParams = new URLSearchParams(searchValues as any).toString();
		response = await axios.get(
			`${API_BASE_URL}/add-requests/${unionId}/send-out?${queryParams}`,
			{ withCredentials: true },
		);
		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitJoinCircleRequest = async (
	payload: IJoinRequestPayload,
	circleId: string,
) => {
	try {
		const url = `${API_BASE_URL}/circle/${circleId}/join-request`;

		const response = await axios.post(url, payload, { withCredentials: true });

		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitFetchJoinCircleRequestsOfCircle = async (
	circleId: string,
) => {
	try {
		const url = `${API_BASE_URL}/circle/${circleId}/join-requests`;

		const response = await axios.get(url, { withCredentials: true });

		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitFetchJoinCircleRequestsOfCircleByCircleLeaderId = async (
	circleLeaderId: string,
) => {
	try {
		const url = `${API_BASE_URL}/circle/leader/${circleLeaderId}/join-requests`;

		const response = await axios.get(url, { withCredentials: true });

		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitJoinRequestUserSent = async (userId: string) => {
	try {
		const url = `${API_BASE_URL}/user/${userId}/join-request/sent`;
		const response = await axios.get(url, { withCredentials: true });
		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitAcceptJoinRequest = async (
	circleId: string,
	requestId: string,
) => {
	try {
		const url = `${API_BASE_URL}/circle/${circleId}/join-requests/${requestId}/approve`;
		const response = await axios.post(url, {}, { withCredentials: true });
		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitDenyJoinRequest = async (
	circleId: string,
	requestId: string,
) => {
	try {
		const url = `${API_BASE_URL}/circle/${circleId}/join-requests/${requestId}/reject`;
		const response = await axios.post(url, {}, { withCredentials: true });
		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};
// deprecated
export const submitGetCircleConnectedAccountBalance = async (
	unionId: string,
) => {
	try {
		const url = `${API_BASE_URL}/union/${unionId}/stripe/balances`;
		const response = await axios.get(url, { withCredentials: true });
		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitGetUnionCycleBalances = async (
	unionId: string,
	cycleId: string,
) => {
	try {
		const url = `${API_BASE_URL}/balance/union/${unionId}/cycle/${cycleId}`;

		const response = await axios.get(url, { withCredentials: true });

		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data?.message || error.response.data)) ||
			error.message ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

// Admin: fetch all circles
export const submitAdminFetchAllCircles = async () => {
	try {
		const response = await axios.get(`${API_BASE_URL}/admin/circles`, {
			withCredentials: true,
		});

		// controller returns { data: { total, circles, generatedAt }, meta }
		return response.data.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitAdminFetchCircleById = async (circleId: string) => {
	try {
		const response = await axios.get(
			`${API_BASE_URL}/admin/circles/${circleId}`,
			{
				withCredentials: true,
			},
		);

		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitAdminUpdateCircleById = async (
	circleId: string,
	payload: any,
) => {
	try {
		const response = await axios.put(
			`${API_BASE_URL}/admin/circles/${circleId}`,
			payload,
			{
				withCredentials: true,
			},
		);

		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitAdminDeleteCircleById = async (circleId: string) => {
	try {
		const response = await axios.delete(
			`${API_BASE_URL}/admin/circles/${circleId}`,
			{
				withCredentials: true,
			},
		);

		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitAdminBanCircle = async (
	circleId: string,
	reason?: string,
) => {
	try {
		const response = await axios.put(
			`${API_BASE_URL}/admin/circles/${circleId}/ban`,
			{ reason },
			{ withCredentials: true },
		);

		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitAdminUnbanCircle = async (circleId: string) => {
	try {
		const response = await axios.put(
			`${API_BASE_URL}/admin/circles/${circleId}/unban`,
			{},
			{ withCredentials: true },
		);

		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitGetCircleExpectedTotalPayOut = async (unionId: string) => {
	try {
		const url = `${API_BASE_URL}/pay-out/expected-payout/${unionId}`;
		const response = await axios.get(url, { withCredentials: true });
		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitGetIdentityVerificationSession = async (userId: string) => {
	try {
		const url = `${API_BASE_URL}/identity-verification`;
		const response = await axios.post<{
			clientSecret: string;
			sessionUrl: string;
		}>(url, { userId }, { withCredentials: true });

		if (isAuthRequiredResponse(response.data)) {
			throw new Error("Your session expired. Please sign in and try again.");
		}

		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			error.message ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export interface IdentityVerificationStatusResponse {
	status: string;
	verified: boolean;
	submitted: boolean;
}

export interface CompleteIdentityVerificationError extends Error {
	httpStatus?: number;
	isBanned?: boolean;
	bannedAt?: string;
	banReason?: string;
}

// Unauthenticated status read, gated by the opaque token embedded in the Stripe return_url.
export const fetchIdentityVerificationStatus = async (
	userId: string,
	token: string,
) => {
	const url = `${API_BASE_URL}/identity-verification/status/${userId}`;
	const response = await axios.get<IdentityVerificationStatusResponse>(url, {
		params: { t: token },
	});

	return response.data;
};

export const completeIdentityVerificationSession = async (userId: string) => {
	try {
		const url = `${API_BASE_URL}/identity-verification/complete`;
		const response = await axios.post<{
			success: boolean;
			verified?: boolean;
			pending?: boolean;
			status?: string;
			message?: string;
			sessionId?: string;
			accountOnboardingUrl?: string;
			expiresAt?: number;
			isBanned?: boolean;
			bannedAt?: string;
			banReason?: string;
		}>(url, { userId }, { withCredentials: true });

		// The interceptor resolves 401s instead of rejecting, so surface it as a real auth failure.
		if (isAuthRequiredResponse(response.data)) {
			const authError = new Error(
				"Session expired",
			) as CompleteIdentityVerificationError;
			authError.httpStatus = 401;
			throw authError;
		}

		return response.data;
	} catch (error: any) {
		if (error?.httpStatus === 401) throw error;

		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";

		const enrichedError = new Error(
			errorMessage,
		) as CompleteIdentityVerificationError;
		// checkAuth reports bans nested under `details`, the controller reports them at top level.
		const data = error.response?.data;
		enrichedError.httpStatus = error.response?.status;
		enrichedError.isBanned = data?.isBanned ?? data?.details?.isBanned;
		enrichedError.bannedAt = data?.bannedAt ?? data?.details?.bannedAt;
		enrichedError.banReason =
			data?.banReason ?? data?.details?.reason;

		throw enrichedError;
	}
};

export const submitCreateAccountLink = (userId: string) => {
	return axios.post(
		`${API_BASE_URL}/stripe/account-link-refresh`,
		{ userId },
		{ withCredentials: true },
	);
};

// export const submitAccountRequirements = async (userId: string) => {
// 	try {
// 		const url = `${API_BASE_URL}/stripe/account-requirements/${userId}`;
// 		const response = await axios.get<{
// 			success?: boolean;
// 			details: {
// 				external_account: string[];
// 				representative: string[];
// 			};
// 			requirements: {
// 				currently_due: string[];
// 				past_due: string[];
// 				eventually_due: string[];
// 			};
// 			identityVerificationStatus?:
// 				| "pending"
// 				| "processing"
// 				| "verified"
// 				| "requires_action"
// 				| "none";
// 			isUserVerified?: boolean;
// 			countryConfig?: {
// 				code: string;
// 				name: string;
// 				requiresSSN: boolean;
// 				nationalIdLabel: string;
// 				currency: string;
// 			} | null;
// 		}>(url, { withCredentials: true });

// 		// Ensure response has the required structure to prevent "Cannot read properties of undefined" errors
// 		const data = response.data || {};
// 		return {
// 			...response,

// 			data: {
// 				success: data.success ?? true,
// 				details: data.details || {
// 					external_account: null,
// 					representative: null,
// 				},
// 				requirements: {
// 					currently_due: data.requirements?.currently_due || [],
// 					past_due: data.requirements?.past_due || [],
// 					eventually_due: data.requirements?.eventually_due || [],
// 				},
// 				identityVerificationStatus: data.identityVerificationStatus ?? "none",
// 				isUserVerified: data.isUserVerified ?? false,
// 				countryConfig: data.countryConfig ?? null,
// 			},
// 		};
// 	} catch (error: any) {
// 		const errorMessage =
// 			(error.response &&
// 				(error.response.data.message || error.response.data)) ||
// 			"Unknown Error Occurred While Making Request";
// 		throw new Error(errorMessage);
// 	}
// };

export const submitAccountRequirements = async (userId: string) => {
	try {
		const url = `${API_BASE_URL}/stripe/account-requirements/${userId}`;
		return await axios.get(url, { withCredentials: true });
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const submitUpdateAccountRequirements = async (
	userId: string,
	payload: {
		ssn_last_4: string;
		dob: {
			day: number;
			month: number;
			year: number;
		};
	},
) => {
	try {
		await axios.put(
			`${API_BASE_URL}/stripe/account-update/${userId}`,
			payload,
			{ withCredentials: true },
		);
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

// only admin can access this
export const getAllCirclesCurrentMonthContribution = async () => {
	try {
		const url = `${API_BASE_URL}/admin/circles/current-month-contributions`;

		const response = await axios.get(url, { withCredentials: true });
		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const getAllCirclesContributionHistory = async () => {
	try {
		const url = `${API_BASE_URL}/admin/circles/contributions-history`;

		const response = await axios.get(url, { withCredentials: true });
		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

// public endpoint - no authentication required
export const getPublicStats = async () => {
	try {
		const url = `${API_BASE_URL}/public/stats`;

		const response = await axios.get(url);
		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Making Request";
		throw new Error(errorMessage);
	}
};

export const getContributionsChartData = async () => {
	try {
		const url = `${API_BASE_URL}/admin/charts/contributions`;
		const response = await axios.get(url, { withCredentials: true });
		return response.data;
	} catch (error: any) {
		throw error;
	}
};

// Get all union leader applications (admin only)
export const getAllUnionLeaderApplications = async (status?: string) => {
	try {
		const url =
			status ?
				`${API_BASE_URL}/applications/union-leader?status=${status}`
				: `${API_BASE_URL}/applications/union-leader`;
		const response = await axios.get(url, { withCredentials: true });
		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Fetching Applications";
		throw new Error(errorMessage);
	}
};

// Approve union leader application (admin only)
export const approveUnionLeaderApplication = async (applicationId: string) => {
	try {
		const url = `${API_BASE_URL}/applications/union-leader/${applicationId}/approve`;
		const response = await axios.put(url, {}, { withCredentials: true });
		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Approving Application";
		throw new Error(errorMessage);
	}
};

// Reject union leader application (admin only)
export const rejectUnionLeaderApplication = async (applicationId: string) => {
	try {
		const url = `${API_BASE_URL}/applications/union-leader/${applicationId}/reject`;
		const response = await axios.put(url, {}, { withCredentials: true });
		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Rejecting Application";
		throw new Error(errorMessage);
	}
};

// Delete union leader application (admin only)
export const deleteUnionLeaderApplication = async (applicationId: string) => {
	try {
		const url = `${API_BASE_URL}/applications/union-leader/${applicationId}`;
		const response = await axios.delete(url, { withCredentials: true });
		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Deleting Application";
		throw new Error(errorMessage);
	}
};

// Get all join requests (admin only)
export const getAllJoinRequestsAsAdmin = async () => {
	try {
		const url = `${API_BASE_URL}/admin/join-requests`;
		const response = await axios.get(url, { withCredentials: true });
		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Fetching Join Requests";
		throw new Error(errorMessage);
	}
};

// Approve join request (admin only)
export const approveJoinRequestAsAdmin = async (joinRequestId: string) => {
	try {
		const url = `${API_BASE_URL}/admin/join-requests/approve`;
		const response = await axios.post(
			url,
			{ joinRequestId },
			{ withCredentials: true },
		);
		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Approving Join Request";
		throw new Error(errorMessage);
	}
};

// Reject join request (admin only)
export const rejectJoinRequestAsAdmin = async (joinRequestId: string) => {
	try {
		const url = `${API_BASE_URL}/admin/join-requests/reject`;
		const response = await axios.post(
			url,
			{ joinRequestId },
			{ withCredentials: true },
		);
		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Rejecting Join Request";
		throw new Error(errorMessage);
	}
};

// Get all add requests (admin only)
export const getAllAddRequestsAsAdmin = async () => {
	try {
		const url = `${API_BASE_URL}/admin/add-requests`;
		const response = await axios.get(url, { withCredentials: true });
		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Fetching Add Requests";
		throw new Error(errorMessage);
	}
};

// Approve add request (admin only)
export const approveAddRequestAsAdmin = async (addRequestId: string) => {
	try {
		const url = `${API_BASE_URL}/admin/add-requests/approve`;
		const response = await axios.post(
			url,
			{ addRequestId },
			{ withCredentials: true },
		);
		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Approving Add Request";
		throw new Error(errorMessage);
	}
};

// Reject add request (admin only)
export const rejectAddRequestAsAdmin = async (addRequestId: string) => {
	try {
		const url = `${API_BASE_URL}/admin/add-requests/reject`;
		const response = await axios.post(
			url,
			{ addRequestId },
			{ withCredentials: true },
		);
		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Rejecting Add Request";
		throw new Error(errorMessage);
	}
};
// Fetch payout order for a circle (accessible by any member of the circle)
export const submitFetchCirclePayoutOrder = async (unionId: string) => {
	try {
		const url = `${API_BASE_URL}/union/${unionId}/payout-order`;
		const response = await axios.get(url, { withCredentials: true });
		return response.data;
	} catch (error: any) {
		const errorMessage =
			(error.response &&
				(error.response.data.message || error.response.data)) ||
			"Unknown Error Occurred While Fetching Payout Order";
		throw new Error(errorMessage);
	}
};